<template>
	<div class="touch-card-group">
		<v-expansion-panels v-model="openKeys" multiple flat class="touch-card-group__panels">
			<v-expansion-panel v-for="node in visibleItems" :key="node.key" :value="node.key" class="touch-card-group__panel" :class="{ 'touch-card-group__panel--leaf': !node.children }">
				<v-expansion-panel-title expand-icon="keyboard_arrow_down" collapse-icon="keyboard_arrow_up">
					<div class="touch-card-group__header">
						<div class="touch-card-group__heading">
							<div class="title" :style="labelStyle">
								<CompanyName v-if="node.deliveryLocationId != null" :params="{ value: node.label, deliveryLocationId: node.deliveryLocationId }" />
								<template v-else>{{ node.label }}</template>
							</div>
							<div v-if="node.countLabel" class="subtitle">{{ node.countLabel }}</div>
						</div>
						<TouchCardMetrics v-if="node.agg" :row="node.agg" :columns="columns" metric-layout="rows" :expanded="isExpanded(node.key)" :aggregate="true" />
					</div>
				</v-expansion-panel-title>

				<v-expansion-panel-text>
					<TouchCardGroup
						v-if="node.children"
						:nodes="node.children"
						:columns="columns"
						:title-fields="titleFields"
						:subtitle-fields="subtitleFields"
						:title-image-field="titleImageField"
						:title-tags-field="titleTagsField"
						:title-product-id-field="titleProductIdField"
						:title-delivery-location-id-field="titleDeliveryLocationIdField"
						:row-key="rowKey"
						:metric-layout="metricLayout"
						:chunk-size="chunkSize"
						:row-class-field="rowClassField"
						:depth="depth + 1"
						@cell-change="emit('cell-change', $event)"
					/>
					<TouchCardList v-else :rows="node.leaves" :columns="columns" :title-fields="titleFields" :subtitle-fields="subtitleFields" :title-image-field="titleImageField" :title-tags-field="titleTagsField" :title-product-id-field="titleProductIdField" :title-delivery-location-id-field="titleDeliveryLocationIdField" :row-key="rowKey" :metric-layout="metricLayout" :chunk-size="chunkSize" :row-class-field="rowClassField" @cell-change="emit('cell-change', $event)" />
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<div v-if="hasMore" ref="sentinelRef" class="touch-card-group__sentinel">
			<v-progress-circular indeterminate size="20" width="2" color="primary" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import TouchCardList from '@/components/agGrid/TouchCardList.vue';
import TouchCardMetrics from '@/components/agGrid/TouchCardMetrics.vue';
import CompanyName from '@/components/common/CompanyName.vue';
import { useProgressiveList } from '@/composables/useProgressiveList';

// Named explicitly so the template can recurse into itself for every extra grouping level.
defineOptions({ name: 'TouchCardGroup' });

const props = defineProps({
	nodes: { type: Array, default: () => [] },
	columns: { type: Array, default: () => [] },
	titleFields: { type: Array, default: () => [] },
	subtitleFields: { type: Array, default: () => [] },
	titleImageField: { type: String, default: null },
	titleTagsField: { type: String, default: null },
	titleProductIdField: { type: String, default: null },
	titleDeliveryLocationIdField: { type: String, default: null },
	rowKey: { type: String, default: 'id' },
	metricLayout: { type: String, default: 'grid' },
	chunkSize: { type: Number, default: 40 },
	rowClassField: { type: String, default: null },
	depth: { type: Number, default: 1 },
});

const emit = defineEmits(['cell-change']);

const { sentinelRef, visibleItems, hasMore } = useProgressiveList(
	() => props.nodes,
	() => props.chunkSize
);

const openKeys = ref([]);
function isExpanded(key) {
	return openKeys.value.includes(key);
}

const MAX_LIGHTEN_PERCENT = 40;

const labelStyle = computed(() => {
	const lightenPercent = Math.min(Math.max(props.depth - 1, 0) * 10, MAX_LIGHTEN_PERCENT);
	if (lightenPercent <= 0) {
		return null;
	}
	return { color: `color-mix(in srgb, rgb(var(--v-theme-primary)) ${100 - lightenPercent}%, white ${lightenPercent}%)` };
});
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.touch-card-group {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&__panel {
		border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
		border-radius: 12px;
		background: rgb(var(--v-theme-surface));
		box-shadow: none;
		animation: touch-card-fade-in 0.28s ease both;

		& + & {
			margin-top: 8px;
		}

		&::after {
			display: none;
		}

		:deep(> .v-expansion-panel-title) {
			padding: 10px 12px;
			min-height: 0;
			gap: 8px;
			border-radius: 10px;
			position: relative;
		}

		:deep(.v-expansion-panel-title__icon) {
			position: absolute;
			top: 10px;
			right: 12px;
			margin: 0;
		}

		:deep(.v-expansion-panel-text__wrapper) {
			padding: 0 8px 8px;
		}

		&--leaf {
			:deep(> .v-expansion-panel-title) {
				position: sticky;
				top: 0;
				z-index: 2;
				background: rgb(var(--v-theme-surface));

				&:hover {
					background: linear-gradient(rgb(var(--v-theme-primary), 20%), rgb(var(--v-theme-primary), 20%)), rgb(var(--v-theme-surface));
				}
			}
		}
	}

	&__header {
		display: flex;
		flex-direction: column;
		gap: 2px;
		width: 100%;
		min-width: 0;
	}

	&__heading {
		display: flex;
		flex-direction: column;
		min-width: 0;
		padding-right: 28px;
	}

	.title {
		font-size: $font-size;
		font-weight: 600;
		color: rgb(var(--v-theme-primary));
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.subtitle {
		font-size: 12px;
		line-height: $font-size;
		color: rgba(var(--v-theme-on-surface), 0.6);
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	&__sentinel {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 40px;
	}

	.touch-card-group:first-child {
		margin-top: 8px;
	}
}

@keyframes touch-card-fade-in {
	from {
		opacity: 0;
		transform: translateY(8px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.touch-card-group__panel {
		animation: none;
	}
}
</style>
